// web/src/lib/post-login-dest.ts
//
// Carries the user's intended destination across the OAuth round trip.
//
// App.tsx renders <GoogleLogin/> *in place of* the requested route rather than
// redirecting to a login URL, so at that moment location.pathname still holds
// where the user was trying to go. GoogleLogin used to discard it and hardcode
// navigate('/'), which is why REGISTER YOUR SHOP dumped café owners on the
// customer dashboard.
//
// localStorage (not sessionStorage): zkLogin already persists its ephemeral
// keypair there across this exact round trip (zklogin-auth.ts:62-64), so the
// survival characteristics are known-good.
//
// This is a redirect target chosen from persisted data, so it is treated as
// untrusted input: path-only, allowlisted against the real route table, and
// time-boxed. Anything unrecognised degrades to '/' rather than throwing —
// a failed redirect must never block a successful login.

export const POST_LOGIN_DEST_KEY = 'bitbrew_post_login_dest';

// Long enough to survive a slow OAuth hop plus a password reset or a 2FA
// detour; short enough that an intent from a previous session cannot fire.
export const POST_LOGIN_DEST_TTL_MS = 30 * 60 * 1000;

// Mirrors the route table in App.tsx. Deliberately excludes /auth/callback
// (never a destination — it is the hop itself) and the Navigate-only stubs
// /shops and /hq, which just bounce to '/'.
const EXACT_ROUTES = new Set([
  '/',
  '/for-shops',
  '/shops/owner/dashboard',
  '/shop/analytics',
  '/shops/register',
  '/shops/settings',
  '/shops/till-kit',
  '/shops/member-cards',
  '/shops/assist',
  '/redeem-comp',
  // An NFC tag tap by a signed-out stranger is the onboarding funnel: they land
  // on /nfc?uid&ctr&cmac, sign in, and must come back here with those params
  // intact to claim the stamp. The query survives — the allowlist below is
  // applied to the pathname only.
  '/nfc',
  '/privacy',
  '/terms',
  '/merchant-terms',
]);

// The one parameterised route: /shops/:id. A single non-empty segment that is
// not one of the literal /shops/* routes already covered above.
const SHOP_DETAIL = /^\/shops\/[A-Za-z0-9_-]+$/;

function isAllowedPathname(pathname: string): boolean {
  if (EXACT_ROUTES.has(pathname)) return true;
  return SHOP_DETAIL.test(pathname) && !EXACT_ROUTES.has(pathname);
}

// Validate a raw stash payload into a safe same-origin path.
// Pure: `now` is injected so the TTL is testable without faking timers.
export function resolvePostLoginDest(raw: string | null, now: number): string {
  if (!raw) return '/';

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return '/';
  }

  if (typeof parsed !== 'object' || parsed === null) return '/';
  const { path, ts } = parsed as { path?: unknown; ts?: unknown };
  if (typeof path !== 'string' || typeof ts !== 'number') return '/';

  // Future timestamps mean a tampered stash or a clock jump — either way we
  // cannot reason about freshness, so refuse.
  const age = now - ts;
  if (age < 0 || age > POST_LOGIN_DEST_TTL_MS) return '/';

  // Must be a single-slash-rooted relative path. '//evil.com' is protocol-
  // relative and would leave the origin, so the second character matters.
  if (!path.startsWith('/') || path.startsWith('//')) return '/';
  if (path.includes('://')) return '/';
  if (path.includes('\\')) return '/';

  // Split off the query; the allowlist applies to the pathname only.
  const queryIndex = path.search(/[?#]/);
  const pathname = queryIndex === -1 ? path : path.slice(0, queryIndex);
  if (!isAllowedPathname(pathname)) return '/';

  return path;
}

// Persist an intended destination. Silently skips anything that would not
// survive resolution, and skips a bare '/' since there is nothing to restore
// — but still clears any previously-stashed destination in that case, so a
// login from '/' never resurrects an earlier abandoned journey.
export function stashPostLoginDest(pathWithSearch: string, now: number): void {
  const candidate = JSON.stringify({ path: pathWithSearch, ts: now });
  const resolved = resolvePostLoginDest(candidate, now);
  if (resolved === '/') {
    try {
      localStorage.removeItem(POST_LOGIN_DEST_KEY);
    } catch {
      /* private mode / quota — nothing more we can do */
    }
    return;
  }
  try {
    localStorage.setItem(POST_LOGIN_DEST_KEY, candidate);
  } catch {
    /* private mode / quota — losing the redirect is not worth failing login */
  }
}

// Read without consuming. Used to pick the login screen's copy variant.
export function peekPostLoginDest(now: number): string {
  try {
    return resolvePostLoginDest(localStorage.getItem(POST_LOGIN_DEST_KEY), now);
  } catch {
    return '/';
  }
}

// Read and consume. A stale intent must never fire on a later unrelated login,
// so the stash is cleared even when it fails to resolve.
export function takePostLoginDest(now: number): string {
  try {
    const raw = localStorage.getItem(POST_LOGIN_DEST_KEY);
    localStorage.removeItem(POST_LOGIN_DEST_KEY);
    return resolvePostLoginDest(raw, now);
  } catch {
    return '/';
  }
}
